import type { StoredOperatorRosterEntry } from '../db'
import { getOfflineDb } from '../dbFactory'

// Crew Mode Charter D-4 shared identity mechanism: local cache of the
// tenant's operator roster (PIN hashes, never raw PINs), refreshed
// opportunistically whenever online — see src/utils/operatorRoster.ts for
// the refresh/verify logic that consumes this repo.
export class DexieOperatorRosterRepo {
  async list(): Promise<StoredOperatorRosterEntry[]> {
    return getOfflineDb().operatorRoster.toArray()
  }

  async getByTenantMembershipId(tenantMembershipId: string): Promise<StoredOperatorRosterEntry | undefined> {
    return getOfflineDb().operatorRoster.get(tenantMembershipId)
  }

  /**
   * Replaces the entire cached roster with `entries`. The roster is a
   * server-authoritative snapshot (who currently has a PIN set for this
   * tenant), not an append log, so a full replace on each refresh is
   * correct — a member whose PIN was cleared server-side should stop being
   * a valid offline PIN target here too.
   */
  async replaceAll(entries: StoredOperatorRosterEntry[]): Promise<void> {
    const db = getOfflineDb()
    await db.transaction('rw', db.operatorRoster, async () => {
      await db.operatorRoster.clear()
      if (entries.length > 0) {
        await db.operatorRoster.bulkPut(entries)
      }
    })
  }

  async clear(): Promise<void> {
    await getOfflineDb().operatorRoster.clear()
  }
}

export const dexieOperatorRosterRepo = new DexieOperatorRosterRepo()
